<template>
  <v-container>
    <v-row align="center">
      <v-col cols="12" sm="auto">
        <v-btn-toggle v-model="viewMode" mandatory dense color="primary">
          <v-btn value="daily">Daily</v-btn>
          <v-btn value="weekly">Weekly</v-btn>
          <v-btn value="monthly">Monthly</v-btn>
        </v-btn-toggle>
      </v-col>

      <v-col cols="12" sm="auto">
        <v-btn
          outlined
          small
          :loading="refreshingStats"
          :disabled="refreshingStats"
          @click="refreshStats"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Refresh stats
        </v-btn>
      </v-col>

      <v-col cols="12" sm="auto" v-if="viewMode === 'daily'">
        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="390px"
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedDate"
              label="Date"
              prepend-icon="mdi-calendar"
              readonly
              hide-details
              dense
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="selectedDate"
            :max="todayStr()"
            no-title
            scrollable
            @input="dateMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col
        cols="12"
        sm="auto"
        class="d-flex align-center"
        v-if="viewMode === 'weekly'"
      >
        <v-btn icon @click="shiftWeek(-1)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-menu
          v-model="weekMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="390px"
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="rangeLabel"
              label="Week"
              prepend-icon="mdi-calendar"
              readonly
              hide-details
              dense
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="selectedWeekDate"
            :max="todayStr()"
            no-title
            scrollable
            @input="weekMenu = false"
          ></v-date-picker>
        </v-menu>
        <v-btn icon :disabled="isCurrentWeek" @click="shiftWeek(1)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-col>

      <v-col
        cols="12"
        sm="auto"
        class="d-flex align-center"
        v-if="viewMode === 'monthly'"
      >
        <v-btn icon @click="shiftMonth(-1)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-menu
          v-model="monthMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          max-width="390px"
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="rangeLabel"
              label="Month"
              prepend-icon="mdi-calendar"
              readonly
              hide-details
              dense
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="selectedMonth"
            type="month"
            :max="todayStr().substring(0, 7)"
            no-title
            @input="monthMenu = false"
          ></v-date-picker>
        </v-menu>
        <v-btn icon :disabled="isCurrentMonth" @click="shiftMonth(1)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="getStatsError">
      <v-col cols="12">
        <v-alert dense outlined text type="error" class="ma-0">
          {{ getStatsError }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row v-if="!getStatsError">
      <v-col xs="12" sm="12" md="6" lg="6" xl="6">
        <v-card>
          <v-card-title>API calls</v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-if="statsLoading">
                <v-skeleton-loader type="card"></v-skeleton-loader>
              </v-col>
              <v-col v-if="!statsLoading">
                <canvas id="apiChart"></canvas>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-chip small
              >{{ periodLabel }} API calls: {{ totalApiCalls }}</v-chip
            >
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col xs="12" sm="12" md="6" lg="6" xl="6">
        <v-card>
          <v-card-title>Errors</v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-if="statsLoading">
                <v-skeleton-loader type="card"></v-skeleton-loader>
              </v-col>
              <v-col v-if="!statsLoading">
                <canvas id="errorChart"></canvas>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-chip small class="mr-2"
              >{{ periodLabel }} errors: {{ totalErrors }}</v-chip
            >
            <v-chip small>
              {{ periodLabel }} critical errors: {{ totalCriticalErrors }}
            </v-chip>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col xs="12" sm="12" md="6" lg="6" xl="6">
        <v-card>
          <v-card-title>Incoming reviews</v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-if="statsLoading">
                <v-skeleton-loader type="card"></v-skeleton-loader>
              </v-col>
              <v-col v-if="!statsLoading">
                <canvas id="incomingReviewsChart"></canvas>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-chip small class="mr-2"
              >{{ periodLabel }} incoming revs:
              {{ totalIncomingReviews }}</v-chip
            >
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col xs="12" sm="12" md="6" lg="6" xl="6">
        <v-card>
          <v-card-title>Done reviews</v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-if="statsLoading">
                <v-skeleton-loader type="card"></v-skeleton-loader>
              </v-col>
              <v-col v-if="!statsLoading">
                <canvas id="doneReviewsChart"></canvas>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-chip small class="mr-2"
              >{{ periodLabel }} done revs: {{ totalDoneReviews }}</v-chip
            >
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-card-title>Users</v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-if="statsLoading">
                <v-skeleton-loader type="card"></v-skeleton-loader>
              </v-col>
              <v-col v-if="!statsLoading">
                <canvas id="usersChart"></canvas>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-chip small class="mr-2"
              >{{ periodLabel }} unique visitors: {{ totalUniqueVisitors }}</v-chip
            >
            <v-chip small class="mr-2"
              >{{ periodLabel }} new unique visitors:
              {{ totalNewUniqueVisitors }}</v-chip
            >
            <v-chip small class="mr-2"
              >{{ periodLabel }} new registrations:
              {{ totalNewRegistrations }}</v-chip
            >
            <v-chip small
              >{{ periodLabel }} subscriptions purchased:
              {{ totalPurchasedSubscriptions }}</v-chip
            >
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-card-title>API entries</v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-if="statsLoading">
                <v-skeleton-loader type="card"></v-skeleton-loader>
              </v-col>
              <v-col v-if="!statsLoading">
                <canvas id="apiEntriesChart"></canvas>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Chart from "chart.js/auto";

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const VIEW_MODES = ["daily", "weekly", "monthly"];

export default {
  props: {
    initialDate: {
      type: String,
      default: null,
    },
    initialMode: {
      type: String,
      default: null,
    },
  },
  data() {
    const mode = VIEW_MODES.includes(this.initialMode)
      ? this.initialMode
      : "daily";
    const anchorDate =
      this.initialDate && ISO_DATE_RE.test(this.initialDate)
        ? this.initialDate
        : this.todayStr();

    return {
      viewMode: mode,
      dates: [],

      selectedDate: mode === "daily" ? anchorDate : this.todayStr(),
      selectedWeekDate: mode === "weekly" ? anchorDate : this.todayStr(),
      selectedMonth:
        mode === "monthly"
          ? anchorDate.substring(0, 7)
          : this.todayStr().substring(0, 7),

      dateMenu: false,
      weekMenu: false,
      monthMenu: false,

      statsLoading: true,
      getStatsError: "",
      refreshingStats: false,
      series: {},
      hourLabels: [],
      chartLabels: [],
      usersChartLabels: [],
      apiEntries: new Map(),

      totalApiCalls: 0,
      totalIncomingReviews: 0,
      totalDoneReviews: 0,
      totalErrors: 0,
      totalCriticalErrors: 0,
      totalUniqueVisitors: 0,
      totalNewUniqueVisitors: 0,
      totalNewRegistrations: 0,
      totalPurchasedSubscriptions: 0,

      apiChartInstance: null,
      errorChartInstance: null,
      incomingReviewsChartInstance: null,
      doneReviewsChartInstance: null,
      apiEntriesChartInstance: null,
      usersChartInstance: null,
    };
  },
  computed: {
    periodLabel() {
      return { daily: "Daily", weekly: "Weekly", monthly: "Monthly" }[
        this.viewMode
      ];
    },
    rangeLabel() {
      if (!this.dates.length) {
        return "";
      }
      if (this.viewMode === "monthly") {
        return this.formatMonthYear(this.dates[0]);
      }
      return `${this.formatLongDate(this.dates[0])} - ${this.formatLongDate(
        this.dates[this.dates.length - 1],
      )}`;
    },
    isCurrentWeek() {
      return (
        this.mondayOf(this.selectedWeekDate) === this.mondayOf(this.todayStr())
      );
    },
    isCurrentMonth() {
      return this.selectedMonth === this.todayStr().substring(0, 7);
    },
  },
  async mounted() {
    this.initLabels();
    await this.reload();
  },
  watch: {
    viewMode() {
      this.syncQuery();
      this.reload();
    },
    selectedDate(newDate, oldDate) {
      if (newDate === oldDate) return;
      this.syncQuery();
      this.reload();
    },
    selectedWeekDate(newDate, oldDate) {
      if (newDate === oldDate) return;
      this.syncQuery();
      this.reload();
    },
    selectedMonth(newMonth, oldMonth) {
      if (newMonth === oldMonth) return;
      this.syncQuery();
      this.reload();
    },
  },
  methods: {
    formatDateLocal(d) {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${d.getFullYear()}-${mm}-${dd}`;
    },
    todayStr() {
      return this.formatDateLocal(new Date());
    },
    mondayOf(dateStr) {
      const d = new Date(dateStr + "T00:00:00");
      const offset = (d.getDay() + 6) % 7; // days since Monday
      d.setDate(d.getDate() - offset);
      return this.formatDateLocal(d);
    },
    formatLongDate(dateStr) {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    formatMonthYear(dateStr) {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      });
    },
    shortDateLabel(dateStr) {
      const [, m, d] = dateStr.split("-");
      return `${m}/${d}`;
    },
    shiftWeek(delta) {
      const d = new Date(this.selectedWeekDate + "T00:00:00");
      d.setDate(d.getDate() + delta * 7);
      const candidate = this.formatDateLocal(d);
      this.selectedWeekDate =
        candidate > this.todayStr() ? this.todayStr() : candidate;
    },
    shiftMonth(delta) {
      const [y, m] = this.selectedMonth.split("-").map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      const candidate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}`;
      const maxMonth = this.todayStr().substring(0, 7);
      this.selectedMonth = candidate > maxMonth ? maxMonth : candidate;
    },
    currentAnchorDate() {
      if (this.viewMode === "monthly") {
        return `${this.selectedMonth}-01`;
      }
      if (this.viewMode === "weekly") {
        return this.selectedWeekDate;
      }
      return this.selectedDate;
    },
    syncQuery() {
      const query = { ...this.$route.query };
      if (this.viewMode === "daily") {
        delete query.mode;
      } else {
        query.mode = this.viewMode;
      }
      const anchor = this.currentAnchorDate();
      if (this.viewMode === "daily" && anchor === this.todayStr()) {
        delete query.date;
      } else {
        query.date = anchor;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    async reload() {
      await this.getStats();
      this.initAPIChart();
      this.initErrorChart();
      this.initIncomingReviewsChart();
      this.initDoneReviewsChart();
      this.initUsersChart();
      this.initApiEntriesChart();
    },
    initLabels() {
      this.hourLabels = [];
      for (var i = 0; i < 24; i++) {
        var label = i < 12 ? i + "am" : i + "pm";
        this.hourLabels.push(label);
      }
    },
    // Builds this.chartLabels/this.series from the raw per-day counters:
    // a single day shows its own 24 hourly buckets, a range of days shows
    // one aggregated (summed) point per day instead.
    buildSeries(dates, ctrs) {
      const emptyHours = () => new Array(24).fill(0);
      const sumHours = (arr) => (arr || []).reduce((a, b) => a + b, 0);

      if (dates.length <= 1) {
        const ctr = ctrs[0] || {};
        this.chartLabels = this.hourLabels;
        this.series = {
          traffic: ctr.HourlyTraffic || emptyHours(),
          errors: ctr.HourlyErrors || emptyHours(),
          criticalErrors: ctr.HourlyCriticalErrors || emptyHours(),
          incomingBasic: ctr.HourlyIncomingBasicReviews || emptyHours(),
          incomingStandard: ctr.HourlyIncomingStandardReviews || emptyHours(),
          incomingFull: ctr.HourlyIncomingFullReviews || emptyHours(),
          doneBasic: ctr.HourlyDoneBasicReviews || emptyHours(),
          doneStandard: ctr.HourlyDoneStandardReviews || emptyHours(),
          doneFull: ctr.HourlyDoneFullReviews || emptyHours(),
        };
      } else {
        this.chartLabels = dates.map((d) => this.shortDateLabel(d));
        this.series = {
          traffic: ctrs.map((c) => sumHours(c.HourlyTraffic)),
          errors: ctrs.map((c) => sumHours(c.HourlyErrors)),
          criticalErrors: ctrs.map((c) => sumHours(c.HourlyCriticalErrors)),
          incomingBasic: ctrs.map((c) => sumHours(c.HourlyIncomingBasicReviews)),
          incomingStandard: ctrs.map((c) =>
            sumHours(c.HourlyIncomingStandardReviews),
          ),
          incomingFull: ctrs.map((c) => sumHours(c.HourlyIncomingFullReviews)),
          doneBasic: ctrs.map((c) => sumHours(c.HourlyDoneBasicReviews)),
          doneStandard: ctrs.map((c) => sumHours(c.HourlyDoneStandardReviews)),
          doneFull: ctrs.map((c) => sumHours(c.HourlyDoneFullReviews)),
        };
      }

      // Unique visitors only ever have day granularity (there's no hourly
      // breakdown for them), so this always plots one point per date - even
      // in daily mode, where it's a single point/bar.
      this.usersChartLabels = dates.map((d) => this.shortDateLabel(d));
      this.series.uniqueVisitors = ctrs.map((c) => c.UniqueVisitors || 0);
      this.series.newUniqueVisitors = ctrs.map((c) => c.NewUniqueVisitors || 0);
      this.series.newRegistrations = ctrs.map((c) =>
        sumHours(c.HourlyRegisteredUsers),
      );
      this.series.purchasedSubscriptions = ctrs.map((c) =>
        sumHours(c.HourlyPurchasedSubscriptions),
      );
    },
    initAPIChart() {
      if (this.apiChartInstance) {
        this.apiChartInstance.destroy();
      }
      const ac = document.getElementById("apiChart");
      this.apiChartInstance = new Chart(ac, {
        type: "line",
        data: {
          labels: this.chartLabels,
          datasets: [
            {
              label: "# of API calls",
              data: this.series.traffic,
              borderWidth: 3,
              borderColor: "rgb(75, 192, 192)",
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    },
    initErrorChart() {
      if (this.errorChartInstance) {
        this.errorChartInstance.destroy();
      }
      const ec = document.getElementById("errorChart");
      this.errorChartInstance = new Chart(ec, {
        type: "line",
        data: {
          labels: this.chartLabels,
          datasets: [
            {
              label: "# of errors",
              data: this.series.errors,
              borderWidth: 3,
              borderColor: "rgb(176, 2, 2)",
            },
            {
              label: "# of critical errors",
              data: this.series.criticalErrors,
              borderWidth: 3,
              borderColor: "rgb(135, 66, 245)",
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    },
    initIncomingReviewsChart() {
      if (this.incomingReviewsChartInstance) {
        this.incomingReviewsChartInstance.destroy();
      }
      const irc = document.getElementById("incomingReviewsChart");
      this.incomingReviewsChartInstance = new Chart(irc, {
        type: "line",
        data: {
          labels: this.chartLabels,
          datasets: [
            {
              label: "basic reviews",
              data: this.series.incomingBasic,
              borderWidth: 2,
              borderColor: "rgb(255, 165, 0)",
            },
            {
              label: "standard reviews",
              data: this.series.incomingStandard,
              borderWidth: 2,
              borderColor: "rgb(0, 0, 255)",
            },
            {
              label: "full reviews",
              data: this.series.incomingFull,
              borderWidth: 2,
              borderColor: "rgb(128, 0, 128)",
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    },
    initDoneReviewsChart() {
      if (this.doneReviewsChartInstance) {
        this.doneReviewsChartInstance.destroy();
      }
      const drc = document.getElementById("doneReviewsChart");
      this.doneReviewsChartInstance = new Chart(drc, {
        type: "line",
        data: {
          labels: this.chartLabels,
          datasets: [
            {
              label: "basic reviews",
              data: this.series.doneBasic,
              borderWidth: 2,
              borderColor: "rgb(255, 165, 0)",
            },
            {
              label: "standard reviews",
              data: this.series.doneStandard,
              borderWidth: 2,
              borderColor: "rgb(0, 0, 255)",
            },
            {
              label: "full reviews",
              data: this.series.doneFull,
              borderWidth: 2,
              borderColor: "rgb(128, 0, 128)",
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    },
    initUsersChart() {
      if (this.usersChartInstance) {
        this.usersChartInstance.destroy();
      }
      const uc = document.getElementById("usersChart");
      this.usersChartInstance = new Chart(uc, {
        type: "bar",
        data: {
          labels: this.usersChartLabels,
          datasets: [
            {
              label: "unique visitors",
              data: this.series.uniqueVisitors,
              backgroundColor: "rgb(33, 150, 243)",
            },
            {
              label: "new unique visitors",
              data: this.series.newUniqueVisitors,
              backgroundColor: "rgb(76, 175, 80)",
            },
            {
              label: "new registrations",
              data: this.series.newRegistrations,
              backgroundColor: "rgb(255, 152, 0)",
            },
            {
              label: "subscriptions purchased",
              data: this.series.purchasedSubscriptions,
              backgroundColor: "rgb(156, 39, 176)",
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: true,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    },
    initApiEntriesChart() {
      if (this.apiEntriesChartInstance) {
        this.apiEntriesChartInstance.destroy();
      }
      const aec = document.getElementById("apiEntriesChart");
      this.apiEntriesChartInstance = new Chart(aec, {
        type: "bar",
        data: {
          labels: Array.from(this.apiEntries.keys()),
          datasets: [
            {
              label: "# of API entries",
              // backgroundColor: Array.from(this.apiEntries.keys()).map(() =>
              //   randomColorGenerator()
              // ),
              data: Array.from(this.apiEntries.values()),
              borderWidth: 2,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          indexAxis: "y",
          scales: {
            x: {
              beforeUpdate(axis) {
                const labels = axis.chart.data.labels;
                for (let i = 0; i < labels.length; i++) {
                  const lbl = labels[i];
                  if (typeof lbl === "string" && lbl.length > 15) {
                    labels[i] = Array.from(lbl.split("_"));
                  }
                }
              },
            },
          },
        },
      });
    },
    async refreshStats() {
      this.refreshingStats = true;
      try {
        const resp = await this.$utils.callBackendPost(
          this,
          "/admin/refreshStats",
          {},
        );
        this.$emit(
          "notify",
          `Stats refreshed (${resp.processed} entries processed)`,
          false,
        );
        await this.reload();
      } catch (error) {
        const errMsg = this.$utils.getErrorMsg(error);
        this.$emit("notify", "Error refreshing stats: " + errMsg, true);
      } finally {
        this.refreshingStats = false;
      }
    },
    async getStats() {
      this.statsLoading = true;
      this.getStatsError = "";
      try {
        var resp = await this.$utils.callBackendPost(this, "/admin/getStats", {
          date: this.currentAnchorDate(),
          mode: this.viewMode,
        });
        this.dates = resp.dates || [];
        this.buildSeries(this.dates, resp.ctrs || []);
        this.apiEntries = new Map(Object.entries(resp.apiEntries || {}));
        this.countTotals();
      } catch (error) {
        this.getStatsError = error;
      } finally {
        this.statsLoading = false;
      }
    },
    countTotals() {
      const sum = (arr) => arr.reduce((a, b) => a + b, 0);
      this.totalApiCalls = sum(this.series.traffic);
      this.totalErrors = sum(this.series.errors);
      this.totalCriticalErrors = sum(this.series.criticalErrors);
      this.totalIncomingReviews =
        sum(this.series.incomingBasic) +
        sum(this.series.incomingStandard) +
        sum(this.series.incomingFull);
      this.totalDoneReviews =
        sum(this.series.doneBasic) +
        sum(this.series.doneStandard) +
        sum(this.series.doneFull);
      this.totalUniqueVisitors = sum(this.series.uniqueVisitors);
      this.totalNewUniqueVisitors = sum(this.series.newUniqueVisitors);
      this.totalNewRegistrations = sum(this.series.newRegistrations);
      this.totalPurchasedSubscriptions = sum(this.series.purchasedSubscriptions);
    },
  },
};
</script>
<style scoped></style>
